import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Rocket, X, Send, Sparkles, Copy, RefreshCw, Wand2, Save, FolderOpen,
  Check, ArrowRight, Loader2, ExternalLink, Lightbulb, Trash2, Compass, ArrowLeft,
  Download, FileText, Zap, BarChart3, Layers, Clock, Search, Map, Globe, Building2, Users,
} from "lucide-react";
import { toast } from "sonner";
import { BuilderLogo } from "./BuilderLogo";
import { useAuth } from "../hooks/useAuth";
import { usePro } from "../hooks/usePro";
import { ResultPaywall } from "./ai/ResultPaywall";
import { useNotebook } from "../hooks/useNotebook";
import {
  blueprintAdvice, blueprintRecommend, blueprintGenerate,
  saveBlueprint, listBlueprints, getBlueprint, deleteBlueprint, fetchCategories,
  compassRecommend, compassEvent, blueprintMarket, downloadBlueprintPdf, downloadLaunchPlanPdf,
  workflowAudit, workflowSearch, workflowExpand, workflowExplain, workflowCountryIdeas, workflowSmallBusinessIdeas,
  aiSave,
} from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { COUNTRIES, POPULAR_COUNTRIES } from "../lib/countries";

const COUNTRY_BUILD_TYPES = ["AI Startup", "SaaS", "Marketplace", "Mobile App", "Service Business", "Physical Business", "Subscription", "🏪 Small Business", "Doesn't Matter"];
const COUNTRY_AUDIENCES = ["Consumers", "Businesses", "Students", "Tourists", "Healthcare", "Restaurants", "Government", "Everyone"];
const SMALL_BIZ = "🏪 Small Business";
const SMALL_BIZ_CATEGORIES = [
  { key: "Trending Worldwide", emoji: "🔥", desc: "Businesses currently growing around the world." },
  { key: "High-Profit Imports", emoji: "💰", desc: "Successful businesses from other countries that may work in your market." },
  { key: "Fastest Growing Concepts", emoji: "🚀", desc: "New business models gaining popularity globally." },
  { key: "Hidden Gems", emoji: "⭐", desc: "Unique small businesses that are proven but not widely known." },
];

const shortCat = (n) => (!n ? n : n.length <= 16 ? n : n.split(/&|\//)[0].trim());

const COMPASS_STEPS = [
  { key: "role", q: "Which best describes you today?", cards: ["I have a problem I wish someone would solve.", "I want to start a business but don't know what to build.", "I'm exploring startup opportunities.", "I'm ready to build something today."] },
  { key: "audience", q: "Who are you trying to help?", cards: ["Myself", "Businesses", "Students", "Parents", "Travelers", "Drivers", "Pet Owners", "Homeowners", "Farmers", "Healthcare", "Everyone", "Government", "Creators", "Small Businesses"] },
  { key: "problem", q: "What problem do you want to solve?", cards: ["Save Time", "Save Money", "Improve Health", "Increase Safety", "Stay Organized", "Learn Something", "Communication", "Relationships", "Buying & Selling", "Transportation", "Education", "Finance", "Agriculture", "Entertainment", "Travel", "Productivity", "Human Wellness"] },
  { key: "experience", q: "What kind of experience should your startup create?", cards: ["AI Assistant", "Marketplace", "Dashboard", "Social Community", "Learning Platform", "Booking Platform", "Automation Tool", "Business Platform", "Consumer App", "Mobile App", "Web Platform", "Subscription Service"] },
  { key: "frequency", q: "How often will people use your product?", cards: ["Daily", "Weekly", "Monthly", "Only when needed", "I'm not sure"] },
  { key: "revenue", q: "How would you like your business to make money?", cards: ["Subscriptions", "Marketplace Fees", "One-Time Purchase", "Freemium", "Advertising", "Enterprise", "I'm not sure"] },
];

const COMPASS_PROCESSING = [
  "Analyzing startup opportunities...", "Finding market pain...", "Building your Founder Compass...",
  "Comparing startup categories...", "Finding your best match...",
];

// ---- Blueprint AI (guided problem → market → blueprint) ----
const BPAI_PROBLEM_EXAMPLES = [
  "Traffic", "Customer Service", "School", "Banking", "Parenting",
  "Finding Parking", "Bills", "Scheduling", "Fitness", "Healthcare",
];
const BPAI_AUDIENCE = [
  "Teenagers", "Parents", "Businesses", "Teachers", "Students", "Drivers",
  "Travelers", "Pet Owners", "Restaurants", "Hotels", "Healthcare Workers",
  "Construction", "Retail", "Farmers", "Everyone", "Other",
];
const BPAI_PLATFORMS = [
  { name: "RapidNative", url: "https://www.rapidnative.com" },
  { name: "Lovable", url: "https://lovable.dev" },
  { name: "Bolt", url: "https://bolt.new" },
  { name: "Replit", url: "https://replit.com" },
  { name: "Firebase Studio", url: "https://firebase.studio" },
  { name: "Bubble", url: "https://bubble.io" },
  { name: "FlutterFlow", url: "https://flutterflow.io" },
  { name: "Cursor", url: "https://cursor.com" },
  { name: "Other", url: "" },
];
const BPAI_ANALYSIS = [
  "Analyzing...", "Finding market demand...", "Estimating opportunity...", "Searching similar industries...",
];
const BPAI_THINKING = [
  "Building your startup blueprint...", "Designing your MVP...",
  "Preparing your AI build prompt...", "Writing your product specification...",
];
const PROC_MAP = { compass_processing: COMPASS_PROCESSING, bpai_analysis: BPAI_ANALYSIS, bpai_thinking: BPAI_THINKING, wf_building: BPAI_THINKING, aiw_processing: [
  "Analyzing your role...", "Finding time-saving AI...", "Building your workflow audit...", "Estimating hours saved...",
], country_processing: [
  "Analyzing the country's economy...", "Studying local industries & culture...", "Finding market gaps...", "Generating tailored startup ideas...",
] };

// ---- AI Workflow discovery ----
const AIW_PROFESSIONS = ["Teacher", "Realtor", "Accountant", "Restaurant", "Construction", "Marketing", "Nurse", "Lawyer"];
const AIW_SEARCH_EXAMPLES = ["Email", "Scheduling", "Marketing", "Payroll", "Inventory", "Customer Support"];

const WorkflowCard = ({ w, idx, expanded, onToggle, onLearn, onBuild }) => {
  const open = onToggle ? expanded : true;
  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4" data-testid={`workflow-card-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-[#0F0F0F]">{w.name}</p>
          <p className="text-xs text-[#687280] mt-0.5">{w.description || w.solution}</p>
        </div>
        {w.hours_saved != null && (
          <span className="shrink-0 rounded-full bg-[#FBF7EA] border border-[#EBDFB8] px-2.5 py-1 text-[10px] font-semibold text-[#8a6d1a] flex items-center gap-1"><Clock size={11} /> Saves {w.hours_saved} hrs/wk</span>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2.5">
          {w.problem && (<div><p className="text-[10px] font-bold uppercase tracking-wide text-[#D4AF37]">Problem</p><p className="text-xs text-[#374151] mt-0.5">{w.problem}</p></div>)}
          {w.solution && (<div><p className="text-[10px] font-bold uppercase tracking-wide text-[#D4AF37]">AI Solution</p><p className="text-xs text-[#374151] mt-0.5">{w.solution}</p></div>)}
          {Array.isArray(w.tools) && w.tools.length > 0 && (
            <div><p className="text-[10px] font-bold uppercase tracking-wide text-[#D4AF37]">Recommended AI Tools</p>
              <div className="flex flex-wrap gap-1.5 mt-1">{w.tools.map((t) => <span key={t} className="rounded-full bg-[#F7F7F7] border border-gray-100 px-2 py-0.5 text-[11px] text-[#374151]">{t}</span>)}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={() => onLearn?.(w)} className="rounded-full border border-gray-200 py-2 text-[11px] font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid={`workflow-learn-${idx}`}>Learn This Workflow</button>
            <button onClick={() => onBuild?.(w)} className="rounded-full bg-[#0F0F0F] text-white py-2 text-[11px] font-semibold hover:bg-[#1c1c1c]" data-testid={`workflow-build-${idx}`}>Build This Workflow</button>
          </div>
        </div>
      )}
      {onToggle && (
        <button onClick={onToggle} className="mt-3 w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2 text-[11px] font-bold hover:bg-[#c5a030] transition-colors" data-testid={`workflow-view-${idx}`}>{expanded ? "Hide Workflow" : "View Workflow"}</button>
      )}
    </div>
  );
};

const HAVE_STEPS = [
  { key: "idea", q: "Tell me about your idea.", placeholder: "I want to build an app for pet owners..." },
  { key: "problem", q: "What problem are you trying to solve?", placeholder: "The real pain point is..." },
  { key: "audience", q: "Who is this product for?", chips: ["Parents", "Students", "Businesses", "Travelers", "Gamers", "Pet Owners"] },
  { key: "revenue", q: "How would you like to make money?", chips: ["Subscription", "Marketplace", "Commission", "Advertising", "Freemium", "Not Sure"] },
  { key: "platform", q: "Where will people use it?", chips: ["Mobile", "Web", "Desktop", "Both"] },
  { key: "features", q: "What are the three most important features?", placeholder: "1. ...  2. ...  3. ..." },
  { key: "feel", q: "How should the app feel?", chips: ["Luxury", "Minimal", "Professional", "Apple Inspired", "Dark", "Modern", "Fun"] },
  { key: "ai", q: "Would you like AI features inside your app?", chips: ["Chatbot", "Recommendations", "Image Recognition", "Voice Assistant", "Automation", "None"] },
];

const DISCOVERY_STEPS = [
  { key: "interests", q: "What interests you most?", chips: ["AI", "Wellness", "Finance", "Education", "Gaming", "Luxury", "Agriculture", "Marketplace", "Real Estate"] },
  { key: "budget", q: "How much do you want to invest?", chips: ["Under $500", "$500–$5,000", "$5,000+"] },
  { key: "business_type", q: "What kind of business do you want?", chips: ["Mobile App", "Website", "SaaS", "Marketplace", "AI Tool"] },
];

const BUILDERS = [
  { name: "RapidNative", desc: "Prompt to native iOS & Android apps.", url: "https://www.rapidnative.com", recommended: true },
  { name: "Lovable", desc: "AI web app builder.", url: "https://lovable.dev" },
  { name: "Bubble", desc: "No-code web apps.", url: "https://bubble.io" },
  { name: "FlutterFlow", desc: "Visual mobile app builder.", url: "https://flutterflow.io" },
  { name: "Bolt", desc: "Prompt to full-stack app.", url: "https://bolt.new" },
  { name: "Replit", desc: "Code & deploy with AI.", url: "https://replit.com" },
];

const BP_SECTIONS = [
  ["project_name", "Project Name"], ["elevator_pitch", "Elevator Pitch"], ["problem", "Problem Statement"],
  ["solution", "Solution"], ["target_audience", "Target Audience"], ["user_journey", "User Journey"],
  ["revenue_model", "Revenue Model"], ["core_features", "Core Features"], ["recommended_mvp", "Recommended MVP"],
  ["design_style", "Design Style"], ["tech_stack", "Suggested Tech Stack"],
  ["launch_strategy", "Launch Strategy"], ["future_features", "Future Features"],
];

const blueprintToText = (bp) => {
  const line = (label, v) => `## ${label}\n${Array.isArray(v) ? v.map((x) => `- ${x}`).join("\n") : v || ""}\n`;
  let out = `# ${bp.project_name || "Startup Blueprint"}\n\n`;
  BP_SECTIONS.slice(1).forEach(([k, label]) => (out += line(label, bp[k]) + "\n"));
  out += `## Complete AI Builder Prompt\n${bp.builder_prompt || ""}\n`;
  return out;
};

export const BlueprintAI = () => {
  const navigate = useNavigate();
  const { user, requireAuth } = useAuth();
  const { isPro, attempt, openUpgrade } = usePro();
  const { toggleSave, isSaved } = useNotebook();
  const [open, setOpen] = useState(false);
  const [centered, setCentered] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [phase, setPhase] = useState("welcome"); // welcome|interview|reco_loading|reco|generating|blueprint|list|compass|compass_processing|compass_result
  const [mode, setMode] = useState("have_idea");
  const [steps, setSteps] = useState(HAVE_STEPS);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recos, setRecos] = useState([]);
  const [blueprint, setBlueprint] = useState(null);
  const [ideaRef, setIdeaRef] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [myList, setMyList] = useState([]);
  const [catChips, setCatChips] = useState([]);
  const [recoAnswers, setRecoAnswers] = useState(null);
  const [recoOffset, setRecoOffset] = useState(0);
  const [recoBusy, setRecoBusy] = useState(false);
  // Founder Compass
  const [compassIdx, setCompassIdx] = useState(0);
  const [compassAnswers, setCompassAnswers] = useState({});
  const [compassMatch, setCompassMatch] = useState(null);
  const [compassConfidence, setCompassConfidence] = useState(0);
  const [compassExclude, setCompassExclude] = useState([]);
  const [procText, setProcText] = useState(0);
  // Blueprint AI guided flow
  const [bpaiProblem, setBpaiProblem] = useState("");
  const [bpaiAudience, setBpaiAudience] = useState("");
  const [bpaiPlatform, setBpaiPlatform] = useState("");
  const [bpaiMarket, setBpaiMarket] = useState(null);
  // AI Workflow flow
  const [aiwInput, setAiwInput] = useState("");
  const [aiwProfession, setAiwProfession] = useState("");
  const [aiwData, setAiwData] = useState(null);
  const [aiwExpanded, setAiwExpanded] = useState(null);
  const [aiwExtras, setAiwExtras] = useState([]);
  const [aiwSearch, setAiwSearch] = useState("");
  const [aiwSearching, setAiwSearching] = useState(false);
  const [aiwVision, setAiwVision] = useState("");
  const [aiwVisionResult, setAiwVisionResult] = useState(null);
  const [aiwVisionLoading, setAiwVisionLoading] = useState(false);
  // AI Country Ideas flow
  const [countrySel, setCountrySel] = useState(null);   // {name, code, flag}
  const [countryQuery, setCountryQuery] = useState("");
  const [countryBuild, setCountryBuild] = useState("");
  const [countryAudience, setCountryAudience] = useState("");
  const [countryImage, setCountryImage] = useState(null); // base64 from scanner
  const [countryData, setCountryData] = useState(null);
  const [countrySmallCat, setCountrySmallCat] = useState("");
  const [smallBizData, setSmallBizData] = useState(null);
  // Learn / Build a specific workflow
  const [learnWf, setLearnWf] = useState(null);
  const [learnInfo, setLearnInfo] = useState(null);
  const scrollRef = useRef(null);

  // First-visit: auto-open centered after ~2s
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Resume an in-progress Blueprint AI Guided session (e.g. returning from
    // sign-in / Stripe checkout) so no work is lost.
    const raw = sessionStorage.getItem("lbs_bpai_resume");
    if (raw) {
      try {
        const r = JSON.parse(raw);
        if (r && r.market) {
          setBpaiProblem(r.problem || "");
          setBpaiAudience(r.audience || "");
          setBpaiMarket(r.market);
          setMode("blueprint_ai");
          localStorage.setItem("lbs_compass_seen", "1");
          setCentered(false); setOpen(true); setPhase("bpai_market");
          return;
        }
      } catch { /* ignore malformed */ }
    }
    // Auto-open the "How can we help?" chooser ONCE per fresh app session
    // (not on every page/refresh). sessionStorage clears when the tab/app is closed,
    // so it reappears only when the user freshly opens LaunchBS again.
    if (sessionStorage.getItem("lbs_launcher_shown")) return;
    // Don't cover the Learn page's Lesson-of-the-Day card.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/learn")) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("lbs_launcher_shown", "1");
      setMenu(true);
      setPulse(true);
      setTimeout(() => setPulse(false), 1600);
    }, 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => { setPulse(true); setTimeout(() => setPulse(false), 1400); }, 12000);
    return () => clearInterval(t);
  }, []);

  // Chat/interview phases should keep the latest message in view (scroll to bottom);
  // result/display phases (blueprint, market, search results, lists) should land at the TOP.
  const TOP_PHASES = ["blueprint", "bpai_market", "aiw_results", "reco", "compass_result", "list", "country_results", "smallbiz_results"];
  useEffect(() => {
    if (!scrollRef.current) return;
    if (TOP_PHASES.includes(phase)) scrollRef.current.scrollTop = 0;
    else scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, phase, busy]);

  const currentIdea = typeof window !== "undefined" ? sessionStorage.getItem("lbs_current_idea") : null;

  const reset = useCallback(() => {
    setPhase("welcome"); setMode("have_idea"); setSteps(HAVE_STEPS); setStepIdx(0);
    setAnswers({}); setMessages([]); setInput(""); setRecos([]); setBlueprint(null);
    setIdeaRef(null); setSavedId(null);
    setCompassIdx(0); setCompassAnswers({}); setCompassMatch(null); setCompassExclude([]);
    setBpaiProblem(""); setBpaiAudience(""); setBpaiPlatform(""); setBpaiMarket(null);
    setAiwInput(""); setAiwProfession(""); setAiwData(null); setAiwExpanded(null);
    setAiwExtras([]); setAiwSearch(""); setAiwVision(""); setAiwVisionResult(null);
    setCountrySel(null); setCountryQuery(""); setCountryBuild(""); setCountryAudience(""); setCountryImage(null); setCountryData(null); setCountrySmallCat(""); setSmallBizData(null);
    if (typeof window !== "undefined") sessionStorage.removeItem("lbs_bpai_resume");
  }, []);

  const closePanel = () => {
    setOpen(false); setCentered(false);
    if (typeof window !== "undefined") sessionStorage.removeItem("lbs_bpai_resume");
  };

  // Generating a blueprint is FREE for everyone (no login/counter). The full
  // RESULT is paywalled on the result screen for non-Pro founders instead.
  const gateGenerate = useCallback((fn) => {
    fn();
  }, []);

  // Blueprint AI Guided: persist the in-progress answers before gating so the
  // founder lands right back on their Market Opportunity after signing in / subscribing.
  const gateMarketContinue = () => {
    if (!isPro && typeof window !== "undefined") {
      sessionStorage.setItem("lbs_bpai_resume", JSON.stringify({
        problem: bpaiProblem, audience: bpaiAudience, market: bpaiMarket,
      }));
    }
    gateGenerate(() => setPhase("bpai_q3"));
  };

  // ---- AI Workflow discovery ----
  const beginWorkflow = () => {
    setAiwInput(""); setAiwProfession(""); setAiwData(null); setAiwExpanded(null);
    setAiwExtras([]); setAiwSearch(""); setAiwVision(""); setAiwVisionResult(null);
    setMode("ai_workflow"); setPhase("aiw_intro");
    compassEvent("aiw_started");
  };

  const runWorkflowAudit = async () => {
    const prof = aiwInput.trim();
    if (!prof) return;
    setAiwProfession(prof);
    setPhase("aiw_processing");
    try {
      const [data] = await Promise.all([workflowAudit(prof), new Promise((r) => setTimeout(r, 2200))]);
      setAiwData(data); setAiwExtras([]); setAiwExpanded(null); setPhase("aiw_results");
    } catch {
      toast.error("Could not build your workflow audit. Please try again.");
      setPhase("aiw_intro");
    }
  };

  // ---- AI Country Idea Generator ----
  const beginCountryIdeas = (image = null, preselect = null) => {
    setCountrySel(preselect || null); setCountryQuery(preselect ? preselect.name : ""); setCountryBuild(""); setCountryAudience("");
    setCountryData(null); setCountryImage(image || null); setCountrySmallCat(""); setSmallBizData(null);
    setMode("ai_workflow"); setCentered(false); setOpen(true); setPhase("country_setup");
    compassEvent("country_ideas_started");
  };

  const runCountryIdeas = () => {
    if (!countrySel) { toast.error("Pick a country first."); return; }
    const run = async () => {
      setPhase("country_processing");
      const payload = {
        country: countrySel.name, country_code: countrySel.code,
        build_type: countryBuild, audience: countryAudience,
        image_base64: countryImage || "",
      };
      await new Promise((r) => setTimeout(r, 2400));
      for (let i = 0; i < 3; i++) {
        try {
          const data = await workflowCountryIdeas(payload);
          setCountryData(data); setPhase("country_results");
          return;
        } catch {
          if (i < 2) await new Promise((r) => setTimeout(r, 1200)); // silent auto-retry
        }
      }
      toast("Still crafting your ideas — tap Generate once more.");
      setPhase("country_setup");
    };
    gateGenerate(run);
  };

  const buildFromCountryIdea = (idea) => {
    const run = async () => {
      setMode("blueprint_ai"); setBlueprint(null); setSavedId(null); setBpaiPlatform("");
      setCentered(false); setOpen(true); setLearnWf(null); setPhase("wf_building");
      const answers = {
        "What are you building?": idea.name,
        "What problem does it solve?": idea.problem || "",
        "Who is it for?": idea.target_customers || "",
        "Country / market": countrySel ? countrySel.name : "",
        "Revenue model": idea.revenue_model || "",
      };
      try {
        const bp = await blueprintGenerate({ answers, mode: "blueprint_ai" });
        setBlueprint(bp); setPhase("blueprint");
      } catch {
        toast.error("Could not build this blueprint. Try again.");
        setOpen(false);
      }
    };
    gateGenerate(run);
  };

  // ---- Small Business ideas (global, adaptable) ----
  const runSmallBusinessIdeas = (refresh = false) => {
    if (!countrySel) { toast.error("Pick a country first."); return; }
    if (!countrySmallCat) { toast.error("Pick an idea type first."); return; }
    const run = async () => {
      setPhase("country_processing");
      const exclude = refresh && smallBizData ? smallBizData.ideas.map((i) => i.name) : [];
      const payload = { country: countrySel.name, category: countrySmallCat, exclude };
      await new Promise((r) => setTimeout(r, 2400));
      for (let i = 0; i < 3; i++) {
        try {
          const data = await workflowSmallBusinessIdeas(payload);
          setSmallBizData(data); setPhase("smallbiz_results");
          return;
        } catch {
          if (i < 2) await new Promise((r) => setTimeout(r, 1200)); // silent auto-retry
        }
      }
      toast("Still crafting your ideas — tap Generate once more.");
      setPhase("country_setup");
    };
    gateGenerate(run);
  };

  const buildFromSmallBizIdea = (idea) => {
    const run = async () => {
      setMode("blueprint_ai"); setBlueprint(null); setSavedId(null); setBpaiPlatform("");
      setCentered(false); setOpen(true); setLearnWf(null); setPhase("wf_building");
      const answers = {
        "What are you building?": idea.name,
        "What is it (from another country)?": `${idea.name} — a proven concept from ${idea.origin_country}. ${idea.why_works}`,
        "How to adapt it locally": idea.how_local || "",
        "Country / market": countrySel ? countrySel.name : "",
        "Estimated startup cost": idea.startup_cost || "",
      };
      try {
        const bp = await blueprintGenerate({ answers, mode: "blueprint_ai" });
        setBlueprint(bp); setPhase("blueprint");
      } catch {
        toast.error("Could not build this blueprint. Try again.");
        setOpen(false);
      }
    };
    gateGenerate(run);
  };

  const saveIdeaToNotebook = (idea, label) => {
    requireAuth(() => {
      const slug = (idea.name || "idea").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const item = {
        id: `ai-${(countrySel?.code || "xx").toLowerCase()}-${slug}`,
        title: idea.name,
        category_name: `${countrySel?.flag || "🌍"} ${countrySel?.name || ""} · ${label}`,
        ai: true,
      };
      if (isSaved(item.id)) { toast("Already in My Ideas"); return; }
      toggleSave(item);
      toast.success("Saved to My Ideas");
    });
  };
  const saveCountryIdea = (idea) => saveIdeaToNotebook(idea, "Country Idea");
  const saveSmallBizIdea = (idea) => saveIdeaToNotebook(idea, `From ${idea.origin_country || "abroad"}`);

  const runWorkflowSearch = async (q) => {
    const query = (q || aiwSearch).trim();
    if (!query || aiwSearching) return;
    setAiwSearching(true);
    try {      const w = await workflowSearch(query, aiwProfession);
      setAiwExtras((list) => [w, ...list]);
      setAiwSearch("");
    } catch { toast.error("Could not find that workflow. Try again."); }
    finally { setAiwSearching(false); }
  };

  const runWorkflowExpand = async () => {
    const task = aiwVision.trim();
    if (!task || aiwVisionLoading) return;
    setAiwVisionLoading(true);
    try {
      const w = await workflowExpand(task, aiwProfession);
      setAiwVisionResult(w);
    } catch { toast.error("Could not build that workflow. Try again."); }
    finally { setAiwVisionLoading(false); }
  };

  const openLearn = async (w) => {
    setLearnWf(w); setLearnInfo(null);
    try {
      const info = await workflowExplain(w.name, w.description || w.solution || "", w.tools || []);
      setLearnInfo(info);
    } catch {
      setLearnInfo({ what: w.description || w.solution || "", best_for: [], benefits: [], youll_learn: [] });
    }
  };

  const buildWorkflow = (w) => {
    const run = async () => {
      setMode("blueprint_ai"); setBlueprint(null); setSavedId(null); setBpaiPlatform("");
      setCentered(false); setOpen(true); setLearnWf(null); setPhase("wf_building");
      const answers = {
        "What are you building?": w.name,
        "What problem does it solve?": w.problem || w.description || "",
        "How does AI solve it?": w.solution || "",
        "Recommended tools": (w.tools || []).join(", "),
        "Product type": "AI automation workflow / internal tool",
      };
      try {
        const bp = await blueprintGenerate({ answers, mode: "ai_workflow" });
        setBlueprint(bp); setPhase("blueprint");
      } catch {
        toast.error("Could not build this workflow. Try again.");
        setPhase("aiw_results");
      }
    };
    gateGenerate(run);
  };

  // Generate a full blueprint directly from an AI Opportunity Scanner idea.
  const buildFromScannerIdea = (idea) => {
    const run = async () => {
      setMode("blueprint_ai"); setBlueprint(null); setSavedId(null); setBpaiPlatform("");
      setCentered(false); setOpen(true); setLearnWf(null); setPhase("wf_building");
      const answers = {
        "What are you building?": idea.name,
        "What does it do?": idea.description || "",
        "What problem does it solve?": idea.problem || "",
        "Who is it for?": idea.audience || "",
        "Revenue model": idea.revenue_model || "",
        "Product type": idea.build_type || "",
      };
      try {
        const bp = await blueprintGenerate({ answers, mode: "blueprint_ai" });
        setBlueprint(bp); setPhase("blueprint");
      } catch {
        toast.error("Could not build this blueprint. Try again.");
        setOpen(false);
      }
    };
    gateGenerate(run);
  };

  const pushAI = (text) => setMessages((m) => [...m, { role: "ai", text }]);
  const pushUser = (text) => setMessages((m) => [...m, { role: "user", text }]);

  const askStep = (stepList, idx) => {
    if (idx < stepList.length) pushAI(stepList[idx].q);
  };

  const beginHaveIdea = (seedIdea) => {
    setMode("have_idea"); setSteps(HAVE_STEPS);
    if (seedIdea) {
      const a = { idea: seedIdea };
      setAnswers(a); setStepIdx(1);
      setMessages([{ role: "ai", text: HAVE_STEPS[0].q }, { role: "user", text: seedIdea }, { role: "ai", text: HAVE_STEPS[1].q }]);
      setPhase("interview");
    } else {
      setAnswers({}); setStepIdx(0); setMessages([{ role: "ai", text: HAVE_STEPS[0].q }]); setPhase("interview");
    }
  };

  const beginNeedIdea = () => {
    setMode("need_idea"); setSteps(DISCOVERY_STEPS); setStepIdx(0); setAnswers({});
    setRecoOffset(0); setRecoAnswers(null);
    if (catChips.length === 0) {
      fetchCategories()
        .then((cats) => setCatChips(cats.filter((c) => c.count > 0).map((c) => ({ label: shortCat(c.name), value: c.slug }))))
        .catch(() => {});
    }
    setMessages([{ role: "ai", text: "Perfect — let's find the right idea for you. " + DISCOVERY_STEPS[0].q }]);
    setPhase("discovery");
  };

  const refreshRecos = async () => {
    if (!recoAnswers) return;
    setRecoBusy(true);
    const nextOffset = recoOffset + 3;
    try {
      const r = await blueprintRecommend({ ...recoAnswers, offset: nextOffset });
      setRecos(r);
      setRecoOffset(nextOffset);
    } catch { toast.error("Could not refresh ideas."); }
    finally { setRecoBusy(false); }
  };

  // ---- Founder Compass ----
  const beginCompass = () => {
    setCompassIdx(0); setCompassAnswers({}); setCompassMatch(null); setCompassExclude([]);
    setPhase("compass");
    compassEvent("started");
  };

  const runCompass = async (finalAnswers, exclude = []) => {
    setPhase("compass_processing"); setProcText(0);
    try {
      const res = await compassRecommend(finalAnswers, exclude);
      // keep the premium animation visible ~2.8s minimum
      await new Promise((r) => setTimeout(r, 2600));
      if (res.match) {
        setCompassMatch(res.match); setCompassConfidence(res.confidence || 90);
        setCompassExclude((e) => [...e, res.match.id]);
        setPhase("compass_result");
        compassEvent("recommended", { id: res.match.id, title: res.match.title });
        compassEvent("completed");
      } else {
        toast.error("No match found. Try different answers.");
        setPhase("compass");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
      setPhase("compass");
    }
  };

  const selectCompass = (value) => {
    const step = COMPASS_STEPS[compassIdx];
    const next = { ...compassAnswers, [step.key]: value };
    setCompassAnswers(next);
    if (compassIdx + 1 < COMPASS_STEPS.length) {
      setCompassIdx(compassIdx + 1);
    } else {
      runCompass(next, []);
    }
  };

  const showAnotherMatch = () => runCompass(compassAnswers, compassExclude);

  const exploreStartup = () => {
    if (!compassMatch) return;
    compassEvent("explored", { id: compassMatch.id });
    setOpen(false); setCentered(false);
    navigate(`/business/${compassMatch.id}`);
  };

  const compassReasons = () => {
    const a = compassAnswers;
    return [a.audience, a.problem, a.experience, a.revenue, a.frequency].filter((x) => x && !x.startsWith("I'm"));
  };

  // ---- Blueprint AI (guided problem → market → blueprint) ----
  const beginBpai = () => {
    setBpaiProblem(""); setBpaiAudience(""); setBpaiPlatform(""); setBpaiMarket(null);
    setBlueprint(null); setSavedId(null); setMode("blueprint_ai");
    setPhase("bpai_welcome");
    compassEvent("bpai_started");
  };

  // Allow other pages (e.g. Home "Start Your Journey") to open Blueprint AI.
  useEffect(() => {
    const handler = (e) => {
      const m = e.detail?.mode;
      if (m === "scanner_idea" && e.detail?.idea) {
        buildFromScannerIdea(e.detail.idea);
        return;
      }
      if (m === "country") {
        reset();
        beginCountryIdeas(e.detail?.image || null, e.detail?.country || null);
        return;
      }
      reset();
      setCentered(false); setOpen(true); setPulse(false);
      if (m === "blueprint") beginBpai();
      else beginHaveIdea();
    };
    window.addEventListener("lbs:open-blueprint", handler);
    return () => window.removeEventListener("lbs:open-blueprint", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runBpaiMarket = async () => {
    setPhase("bpai_analysis");
    try {
      const [res] = await Promise.all([
        blueprintMarket(bpaiProblem, bpaiAudience),
        new Promise((r) => setTimeout(r, 2600)),
      ]);
      setBpaiMarket(res);
      setPhase("bpai_market");
    } catch {
      toast.error("Could not analyze the market. Please try again.");
      setPhase("bpai_q2");
    }
  };

  const runBpaiBlueprint = async (platform) => {
    setBpaiPlatform(platform);
    setPhase("bpai_thinking");
    const finalAnswers = {
      problem: bpaiProblem,
      audience: bpaiAudience,
      preferred_platform: platform,
      market_opportunity: bpaiMarket || {},
    };
    setAnswers(finalAnswers);
    let bp = null;
    for (let a = 0; a < 2 && !bp; a++) {
      try { bp = await blueprintGenerate({ answers: finalAnswers, mode: "blueprint_ai" }); } catch { /* retry */ }
    }
    if (bp) {
      setBlueprint(bp); setSavedId(null); setPhase("blueprint");
      if (typeof window !== "undefined") sessionStorage.removeItem("lbs_bpai_resume");
    } else {
      toast.error("That took too long. Please try again.");
      setPhase("bpai_q3");
    }
  };

  const continueInBuilder = async () => {
    const b = BPAI_PLATFORMS.find((p) => p.name === bpaiPlatform) || BPAI_PLATFORMS[0];
    await copyToClipboard(blueprint.builder_prompt || blueprintToText(blueprint));
    toast.success(`Prompt copied — paste it into ${b.name}`);
    if (b.url) window.open(b.url, "_blank", "noopener");
  };

  const downloadPdf = async () => {
    try { await downloadBlueprintPdf(blueprint); toast.success("Blueprint PDF downloaded"); }
    catch { toast.error("Could not generate PDF."); }
  };

  const [launchBusy, setLaunchBusy] = useState(false);
  const downloadLaunchPlan = async () => {
    if (launchBusy) return;
    if (!isPro) { attempt("launch_plan"); return; }
    setLaunchBusy(true);
    try {
      await downloadLaunchPlanPdf(blueprint);
      toast.success("Launch Plan downloaded");
    } catch { toast.error("Could not generate Launch Plan. Please try again."); }
    finally { setLaunchBusy(false); }
  };

  const duplicateBlueprint = async (id, e) => {
    e.stopPropagation();
    if (!user) { requireAuth(() => {}); return; }
    try {
      const bp = await getBlueprint(id);
      const copy = await saveBlueprint({
        project_name: `${bp.project_name} (Copy)`, answers: bp.answers, blueprint: bp.blueprint,
        mode: bp.mode, idea_ref: bp.idea_ref, platform: bp.platform, status: "Draft",
      });
      setMyList((l) => [copy, ...l]);
      toast.success("Blueprint duplicated");
    } catch { toast.error("Could not duplicate."); }
  };

  // processing text rotation (compass + blueprint-ai loaders)
  useEffect(() => {
    const list = PROC_MAP[phase];
    if (!list) return;
    setProcText(0);
    const t = setInterval(() => setProcText((i) => (i + 1) % list.length), 800);
    return () => clearInterval(t);
  }, [phase]);

  const submitAnswer = async (value) => {
    const val = (value ?? input).trim();
    if (!val || busy) return;
    setInput("");
    pushUser(val);
    const step = steps[stepIdx];
    const nextAnswers = { ...answers, [step.key]: val };
    setAnswers(nextAnswers);

    if (phase === "discovery") {
      const next = stepIdx + 1;
      if (next < DISCOVERY_STEPS.length) {
        setStepIdx(next);
        setTimeout(() => askStep(DISCOVERY_STEPS, next), 250);
      } else {
        setPhase("reco_loading"); setBusy(true);
        try {
          const r = await blueprintRecommend({ ...nextAnswers, offset: 0 });
          setRecos(r); setRecoAnswers(nextAnswers); setRecoOffset(0);
          pushAI("Based on your answers, here are three ideas from the LaunchBS library that fit you:");
          setPhase("reco");
        } catch {
          toast.error("Could not fetch ideas. Try again.");
          setPhase("discovery");
        } finally { setBusy(false); }
      }
      return;
    }

    // interview (have_idea): advice then next question
    setBusy(true);
    try {
      const advice = await blueprintAdvice(step.q, val);
      setMessages((m) => [...m, { role: "advice", text: advice }]);
    } catch { /* skip advice */ }
    setBusy(false);

    const next = stepIdx + 1;
    if (next < HAVE_STEPS.length) {
      setStepIdx(next);
      setTimeout(() => askStep(HAVE_STEPS, next), 300);
    } else {
      gateGenerate(() => generate(nextAnswers));
    }
  };

  const generate = async (finalAnswers) => {
    setPhase("generating"); setBusy(true);
    pushAI("Building your professional blueprint…");
    let bp = null;
    for (let tries = 0; tries < 2 && !bp; tries++) {
      try {
        bp = await blueprintGenerate({ answers: finalAnswers, mode, idea_ref: ideaRef });
      } catch { /* retry once */ }
    }
    setBusy(false);
    if (bp) {
      setBlueprint(bp); setSavedId(null); setPhase("blueprint");
    } else {
      toast.error("That took too long. Tap Regenerate to try again.");
      pushAI("Hmm, that took longer than expected. Tap the send button once more to retry your blueprint.");
      setStepIdx(HAVE_STEPS.length - 1); setPhase("interview");
    }
  };

  const buildFromIdea = (idea) => {
    setIdeaRef({ id: idea.id, title: idea.title, summary: idea.summary });
    beginHaveIdea(`Build something like ${idea.title}: ${idea.summary}`);
  };

  const copyBlueprint = async () => {
    const ok = await copyToClipboard(blueprintToText(blueprint));
    if (ok) toast.success("Blueprint copied to clipboard");
    else toast.error("Could not copy. Please copy manually.");
  };

  const openBuilder = async (b) => {
    await copyToClipboard(blueprint.builder_prompt || blueprintToText(blueprint));
    toast.success(`Prompt copied — paste it into ${b.name}`);
    if (b.url) window.open(b.url, "_blank", "noopener");
  };

  const doSave = async () => {
    if (!user) { requireAuth(() => {}); toast.message("Sign in to save your blueprint to My Blueprints."); return; }
    setBusy(true);
    try {
      const saved = await saveBlueprint({ id: savedId, project_name: blueprint.project_name, answers, blueprint, mode, idea_ref: ideaRef, platform: bpaiPlatform || null, status: "Ready" });
      setSavedId(saved.id);
      toast.success("Saved to My Blueprints");
    } catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };

  const openList = async () => {
    if (!user) { requireAuth(() => {}); return; }
    setPhase("list"); setBusy(true);
    try { setMyList(await listBlueprints()); } catch { setMyList([]); }
    finally { setBusy(false); }
  };

  const openSaved = async (id) => {
    setBusy(true);
    try {
      const bp = await getBlueprint(id);
      setBlueprint(bp.blueprint); setAnswers(bp.answers || {}); setMode(bp.mode || "have_idea");
      setIdeaRef(bp.idea_ref || null); setSavedId(bp.id); setBpaiPlatform(bp.platform || ""); setPhase("blueprint");
    } catch { toast.error("Could not open blueprint."); }
    finally { setBusy(false); }
  };

  const removeSaved = async (id, e) => {
    e.stopPropagation();
    try { await deleteBlueprint(id); setMyList((l) => l.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete."); }
  };

  const [menu, setMenu] = useState(false);
  const launcherOptions = [
    { key: "blueprint", label: "Create Blueprint to Build", desc: "Turn your idea into a full build plan", icon: Rocket, fn: () => { setMenu(false); reset(); setCentered(false); setPulse(false); setOpen(true); beginBpai(); } },
    { key: "idea", label: "I Have an Idea", desc: "Describe it — we'll help you build it", icon: Lightbulb, fn: () => { setMenu(false); reset(); setCentered(false); setPulse(false); setOpen(true); beginHaveIdea(null); } },
    { key: "compass", label: "Founder Compass", desc: "Find the startup idea that fits you", icon: Compass, fn: () => { setMenu(false); reset(); setCentered(false); setPulse(false); setOpen(true); beginCompass(); } },
    { key: "workflow", label: "AI Workflow", desc: "Discover AI workflows for your work", icon: Zap, fn: () => { setMenu(false); reset(); setCentered(false); setPulse(false); setOpen(true); beginWorkflow(); } },
    { key: "validate", label: "Validate Idea", desc: "Score & validate before you build", icon: BarChart3, fn: () => { setMenu(false); navigate("/ai-resources/validation-lab"); } },
    { key: "scanner", label: "Advanced Opportunity Scanner", desc: "Turn a photo into startup ideas", icon: Search, fn: () => { setMenu(false); navigate("/ai-resources/opportunity-scanner"); } },
  ];


  const progress = phase === "interview" ? Math.round((stepIdx / HAVE_STEPS.length) * 100) : 0;
  const activeStep = (phase === "interview" || phase === "discovery") ? steps[stepIdx] : null;

  return (
    <>
      <style>{`
        @keyframes bp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes bp-pulse-ring { 0%{transform:scale(.9);opacity:.6} 100%{transform:scale(1.7);opacity:0} }
        @keyframes bp-in { from{opacity:0;transform:translateY(16px) scale(.98)} to{opacity:1;transform:translateY(0) scale(1)} }
        .bp-msg-in{animation:bp-in .28s ease both}
        @keyframes bp-pop-center { 0%{opacity:0;transform:translate(-50%,-50%) scale(.85)} 60%{opacity:1;transform:translate(-50%,-50%) scale(1.02)} 100%{opacity:1;transform:translate(-50%,-50%) scale(1)} }
        .bp-pop-center{animation:bp-pop-center .42s cubic-bezier(.22,1,.36,1) both}
      `}</style>

      {/* Floating launcher + 4-option chooser card */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-[60]" data-testid="blueprint-ai-launcher">
          {menu && (
            <>
              <div className="fixed inset-0 z-[59]" onClick={() => setMenu(false)} data-testid="launcher-backdrop" />
              <div className="bp-msg-in absolute bottom-full right-0 mb-3 w-[290px] rounded-3xl border border-white/40 bg-white/95 backdrop-blur-xl shadow-[0_24px_70px_rgba(0,0,0,0.28)] overflow-hidden z-[60]" data-testid="launcher-menu">
                <div className="bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white px-4 py-3.5 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center"><Rocket size={16} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm leading-tight">How can we help?</p>
                    <p className="text-[10px] text-[#c9c39f]">Pick where you'd like to start</p>
                  </div>
                  <button onClick={() => setMenu(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="launcher-close"><X size={16} /></button>
                </div>
                <div className="p-2.5 space-y-1.5 max-h-[62vh] overflow-y-auto">
                  {launcherOptions.map((o) => (
                    <button
                      key={o.key}
                      onClick={o.fn}
                      data-testid={`launcher-${o.key}`}
                      className="w-full flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-2.5 text-left hover:border-[#D4AF37] hover:bg-[#FBF7EA] hover:shadow-sm transition-all group"
                    >
                      <span className="w-9 h-9 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><o.icon size={17} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-sm text-[#0F0F0F] leading-tight">{o.label}</span>
                        <span className="block text-[11px] text-[#687280] mt-0.5">{o.desc}</span>
                      </span>
                      <ArrowRight size={14} className="text-[#c9c39f] group-hover:text-[#B8901F] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div className="relative group">
            {pulse && !menu && <span className="absolute inset-0 rounded-full bg-[#D4AF37]" style={{ animation: "bp-pulse-ring 1.4s ease-out" }} />}
            <button
              onClick={() => setMenu((m) => !m)}
              style={{ animation: menu ? "none" : "bp-float 4s ease-in-out infinite" }}
              className="relative flex items-center gap-2 rounded-full bg-gradient-to-br from-[#D4AF37] via-[#e6c65a] to-[#b8901f] text-[#0F0F0F] pl-3.5 pr-4 py-3 shadow-[0_10px_30px_rgba(184,144,31,0.45)] hover:shadow-[0_14px_40px_rgba(184,144,31,0.6)] transition-shadow"
              data-testid="blueprint-ai-open-btn"
            >
              {menu ? <X size={20} /> : <Rocket size={20} />}
              <span className="font-semibold text-sm hidden sm:inline">Blueprint AI</span>
            </button>
          </div>
        </div>
      )}

      {/* Panel */}
      {open && (
        <>
          {centered && <div className="fixed inset-0 z-[59] bg-black/50 backdrop-blur-sm" onClick={closePanel} data-testid="blueprint-backdrop" />}
          <div className={`fixed z-[60] flex flex-col overflow-hidden border border-white/40 shadow-[0_24px_70px_rgba(0,0,0,0.28)] bg-white/95 backdrop-blur-xl ${centered ? "bp-pop-center left-1/2 top-1/2 w-[94vw] sm:w-[460px] h-[86vh] sm:h-[660px] rounded-3xl" : "bp-msg-in inset-x-0 bottom-0 sm:inset-auto sm:bottom-5 sm:right-5 w-full sm:w-[420px] h-[88vh] sm:h-[640px] rounded-t-3xl sm:rounded-3xl"}`} data-testid="blueprint-ai-panel">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center">
              <Rocket size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold leading-tight">Blueprint AI</p>
              <p className="text-[11px] text-[#c9c39f]">Your 24/7 startup advisor</p>
            </div>
            <button onClick={openList} title="My Blueprints" className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="blueprint-my-list-btn">
              <FolderOpen size={17} />
            </button>
            <button onClick={reset} title="New" className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="blueprint-new-btn">
              <Sparkles size={16} />
            </button>
            <button onClick={() => setOpen(false)} title="Close" className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="blueprint-close-btn">
              <X size={18} />
            </button>
          </div>

          {/* Progress */}
          {phase === "interview" && (
            <div className="px-5 py-2.5 border-b border-gray-100 bg-white" data-testid="blueprint-progress">
              <div className="flex items-center justify-between text-[11px] font-semibold text-[#687280] mb-1.5">
                <span>Blueprint Progress</span><span>{progress}%</span>
              </div>
              <div className="flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className={`h-1.5 flex-1 rounded-full ${i < Math.round(progress / 10) ? "bg-[#D4AF37]" : "bg-gray-200"}`} />
                ))}
              </div>
            </div>
          )}

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAF8]">
            {phase === "welcome" && (
              <div className="bp-msg-in">
                <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">Welcome, Founder.</h3>
                <p className="text-sm text-[#687280] mt-2 leading-relaxed">
                  I'm Blueprint AI — think of me as your startup advisor. Whether you have an idea or need one,
                  I'll help you turn it into a professional blueprint you can build today.
                </p>
                {currentIdea && (
                  <button
                    onClick={() => buildFromIdea({ id: "", title: currentIdea, summary: `the ${currentIdea} concept` })}
                    className="mt-3 w-full text-left rounded-xl border border-[#D4AF37]/40 bg-[#FBF7EA] px-4 py-3 text-sm font-medium text-[#8a6d1a] hover:bg-[#f7efd6] transition-colors"
                    data-testid="blueprint-context-cta"
                  >
                    ✨ Want to build something like <b>{currentIdea}</b>, or your own version?
                  </button>
                )}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <button onClick={beginCompass} className="rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all" data-testid="blueprint-need-idea-btn">
                    <Compass size={20} className="text-[#D4AF37]" />
                    <p className="font-semibold text-sm text-[#0F0F0F] mt-2">Founder Compass</p>
                    <p className="text-xs text-[#687280] mt-0.5">Find your best match.</p>
                  </button>
                  <button onClick={() => beginHaveIdea(null)} className="rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all" data-testid="blueprint-have-idea-btn">
                    <Rocket size={20} className="text-[#D4AF37]" />
                    <p className="font-semibold text-sm text-[#0F0F0F] mt-2">I Have an Idea</p>
                    <p className="text-xs text-[#687280] mt-0.5">Help me build it.</p>
                  </button>
                </div>
                <button onClick={beginBpai} className="mt-3 w-full rounded-2xl border border-[#D4AF37]/50 bg-gradient-to-br from-[#FBF7EA] to-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all flex items-center gap-3" data-testid="blueprint-ai-flow-btn">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center shrink-0">
                    <Sparkles size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0F0F0F] flex items-center gap-1.5">Blueprint AI <span className="text-[9px] bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">GUIDED</span></p>
                    <p className="text-xs text-[#687280] mt-0.5">Turn a real problem into a full startup blueprint.</p>
                  </div>
                </button>
                <button onClick={beginWorkflow} className="mt-3 w-full rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all flex items-center gap-3" data-testid="ai-workflow-btn">
                  <div className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0">
                    <Zap size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0F0F0F]">AI Workflow</p>
                    <p className="text-xs text-[#687280] mt-0.5">Discover AI workflows that can save you 5+ hours every week.</p>
                  </div>
                </button>
                <button onClick={() => beginCountryIdeas()} className="mt-3 w-full rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all flex items-center gap-3" data-testid="ai-country-ideas-btn">
                  <div className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0">
                    <Globe size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0F0F0F] flex items-center gap-1.5">🌍 AI Country Ideas <span className="text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">NEW</span></p>
                    <p className="text-xs text-[#687280] mt-0.5">10 startup ideas tailored to any country's market.</p>
                  </div>
                </button>
              </div>
            )}

            {/* Founder Compass — 6-question wizard */}
            {phase === "compass" && (() => {
              const step = COMPASS_STEPS[compassIdx];
              const longCards = step.cards.some((c) => c.length > 24);
              return (
                <div className="bp-msg-in" data-testid="compass-wizard">
                  <div className="flex items-center justify-between mb-3">
                    <button onClick={() => (compassIdx > 0 ? setCompassIdx(compassIdx - 1) : reset())} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors" data-testid="compass-back-btn">
                      <ArrowLeft size={14} /> Back
                    </button>
                    <span className="text-[11px] font-semibold text-[#D4AF37]">Question {compassIdx + 1} of {COMPASS_STEPS.length}</span>
                  </div>
                  <div className="flex gap-1 mb-4">
                    {COMPASS_STEPS.map((_, i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= compassIdx ? "bg-[#D4AF37]" : "bg-gray-200"}`} />
                    ))}
                  </div>
                  <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] leading-snug">{step.q}</h3>
                  {longCards ? (
                    <div className="grid grid-cols-1 gap-2 mt-4">
                      {step.cards.map((c) => (
                        <button key={c} onClick={() => selectCompass(c)} className="group w-full text-left rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-medium text-[#1f2937] hover:border-[#D4AF37] hover:bg-[#FBF7EA] hover:shadow-md transition-all flex items-center justify-between" data-testid={`compass-option-${c}`}>
                          <span>{c}</span>
                          <ArrowRight size={15} className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {step.cards.map((c) => (
                        <button key={c} onClick={() => selectCompass(c)} className="rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-[#1f2937] hover:border-[#D4AF37] hover:bg-[#FBF7EA] hover:shadow-md transition-all" data-testid={`compass-option-${c}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Loaders — Founder Compass + Blueprint AI */}
            {PROC_MAP[phase] && (
              <div className="flex flex-col items-center justify-center h-full py-10 bp-msg-in" data-testid={phase === "compass_processing" ? "compass-processing" : "bpai-processing"}>
                <div className="relative w-20 h-20 flex items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-[#D4AF37]/25" style={{ animation: "bp-pulse-ring 1.6s ease-out infinite" }} />
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center" style={{ animation: "bp-float 2.5s ease-in-out infinite" }}>
                    {phase === "compass_processing" ? <Compass size={28} className="animate-spin" style={{ animationDuration: "3s" }} /> : phase === "country_processing" ? <Globe size={28} className="animate-spin" style={{ animationDuration: "3s" }} /> : <Sparkles size={28} />}
                  </div>
                </div>
                <p className="mt-6 text-sm font-semibold text-[#0F0F0F] text-center px-6">{PROC_MAP[phase][procText]}</p>
                <p className="mt-1 text-xs text-[#687280]">{phase === "compass_processing" ? "Building your Founder Compass…" : phase === "bpai_analysis" ? "Estimating your market opportunity…" : phase === "aiw_processing" ? "Auditing your time-savers…" : phase === "country_processing" ? "Analyzing the local market…" : "Blueprint AI is building…"}</p>
              </div>
            )}

            {/* Founder Compass — match result */}
            {phase === "compass_result" && compassMatch && (
              <div className="space-y-3 bp-msg-in" data-testid="compass-result">
                <div className="text-center">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-[#FBF7EA] border border-[#EBDFB8] px-3 py-1 text-[11px] font-semibold text-[#8a6d1a]">
                    <Sparkles size={12} className="text-[#D4AF37]" /> {compassConfidence}% Match
                  </div>
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F] mt-2">We Found Your Best Startup Match</h3>
                </div>
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white px-4 py-3">
                    <p className="text-[10px] uppercase tracking-wide text-[#c9c39f]">{compassMatch.category_name}</p>
                    <p className="font-display text-lg font-bold">{compassMatch.title}</p>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-[#374151] leading-relaxed">{compassMatch.summary}</p>
                    {compassReasons().length > 0 && (
                      <div className="mt-3">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">Why this fits you</p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {compassReasons().map((r) => (
                            <span key={r} className="rounded-full bg-[#F7F7F7] border border-gray-100 px-2.5 py-1 text-[11px] text-[#374151]">{r}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button onClick={exploreStartup} className="w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors flex items-center justify-center gap-1.5" data-testid="compass-explore-btn">
                  Explore This Startup <ArrowRight size={16} />
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={showAnotherMatch} className="rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#0F0F0F] flex items-center justify-center gap-1.5" data-testid="compass-another-btn">
                    <RefreshCw size={13} /> Another Match
                  </button>
                  <button onClick={() => beginHaveIdea(null)} className="rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#0F0F0F] flex items-center justify-center gap-1.5" data-testid="compass-have-idea-btn">
                    <Rocket size={13} /> I Have an Idea
                  </button>
                </div>
                <button onClick={() => { closePanel(); navigate("/ai-resources/roadmap"); }} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a]" data-testid="compass-roadmap-btn"><Map size={13} /> Founders Roadmap</button>
              </div>
            )}

            {/* Blueprint AI — welcome */}
            {phase === "bpai_welcome" && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4 bp-msg-in" data-testid="bpai-welcome">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center mb-4"><Sparkles size={26} /></div>
                <h3 className="font-display text-2xl font-extrabold text-[#0F0F0F]">Let's build something that matters.</h3>
                <p className="text-sm text-[#687280] mt-3 leading-relaxed max-w-[300px]">The best startups often begin with a problem that keeps showing up. Let's discover yours.</p>
                <button onClick={() => setPhase("bpai_q1")} className="mt-6 rounded-full bg-[#D4AF37] text-[#0F0F0F] px-10 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="bpai-start-btn">Start</button>
              </div>
            )}

            {/* Blueprint AI — Q1 problem */}
            {phase === "bpai_q1" && (
              <div className="bp-msg-in" data-testid="bpai-q1">
                <button onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] mb-3 transition-colors"><ArrowLeft size={14} /> Back</button>
                <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] leading-snug">What frustrates you at least once a week?</h3>
                <p className="text-sm text-[#687280] mt-1">Think about something you wish worked better.</p>
                <textarea
                  value={bpaiProblem}
                  onChange={(e) => setBpaiProblem(e.target.value)}
                  rows={4}
                  placeholder="Describe the problem in your own words…"
                  className="mt-3 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] resize-none"
                  data-testid="bpai-problem-input"
                />
                <p className="text-[11px] font-semibold text-[#687280] uppercase mt-3 mb-1.5">Examples</p>
                <div className="flex flex-wrap gap-1.5">
                  {BPAI_PROBLEM_EXAMPLES.map((c) => (
                    <button key={c} onClick={() => setBpaiProblem(c)} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors" data-testid={`bpai-example-${c}`}>{c}</button>
                  ))}
                </div>
                <button onClick={() => setPhase("bpai_ai1")} disabled={!bpaiProblem.trim()} className="mt-5 w-full rounded-full bg-[#0F0F0F] text-white py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors disabled:opacity-40" data-testid="bpai-q1-continue">Continue</button>
              </div>
            )}

            {/* Blueprint AI — AI encouragement */}
            {phase === "bpai_ai1" && (
              <div className="bp-msg-in" data-testid="bpai-ai1">
                <div className="rounded-2xl bg-[#FBF7EA] border border-[#EBDFB8] p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a] flex items-center gap-1.5"><Sparkles size={13} className="text-[#D4AF37]" /> Blueprint AI</p>
                  <p className="text-sm text-[#5c4a12] mt-2 leading-relaxed font-semibold">Bingo… now we're onto something.</p>
                  <p className="text-sm text-[#8a6d1a] mt-1.5 leading-relaxed">The best businesses usually begin with a problem worth solving. Let's see how many other people experience this.</p>
                </div>
                <button onClick={() => setPhase("bpai_q2")} className="mt-5 w-full rounded-full bg-[#0F0F0F] text-white py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors" data-testid="bpai-ai1-continue">Continue</button>
              </div>
            )}

            {/* Blueprint AI — Q2 audience */}
            {phase === "bpai_q2" && (
              <div className="bp-msg-in" data-testid="bpai-q2">
                <button onClick={() => setPhase("bpai_q1")} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] mb-3 transition-colors"><ArrowLeft size={14} /> Back</button>
                <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] leading-snug">Besides you… who else experiences this problem?</h3>
                <div className="flex flex-wrap gap-2 mt-4">
                  {BPAI_AUDIENCE.map((c) => (
                    <button key={c} onClick={() => setBpaiAudience(c)} className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-all ${bpaiAudience === c ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a]" : "border-gray-200 bg-white text-[#1f2937] hover:border-[#D4AF37] hover:bg-[#FBF7EA]"}`} data-testid={`bpai-audience-${c}`}>{c}</button>
                  ))}
                </div>
                <button onClick={runBpaiMarket} disabled={!bpaiAudience} className="mt-5 w-full rounded-full bg-[#0F0F0F] text-white py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors disabled:opacity-40" data-testid="bpai-q2-continue">Continue</button>
              </div>
            )}

            {/* Blueprint AI — Market Opportunity */}
            {phase === "bpai_market" && bpaiMarket && (
              <div className="space-y-3 bp-msg-in" data-testid="bpai-market">
                <div className="flex items-center gap-2">
                  <BarChart3 size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">Market Opportunity</h3>
                </div>
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">Potential Audience</p>
                  <p className="text-sm text-[#374151] mt-1 leading-relaxed">{bpaiMarket.audience_summary}</p>
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mt-3">Why this matters</p>
                  <ul className="mt-1 space-y-1">
                    {(bpaiMarket.why_matters || []).map((r, i) => (
                      <li key={i} className="text-sm text-[#374151] flex gap-1.5"><Check size={13} className="mt-1 shrink-0 text-[#D4AF37]" />{r}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-4 text-center">
                  <p className="text-[11px] uppercase tracking-wide text-[#c9c39f]">Opportunity Score</p>
                  <p className="font-display text-4xl font-extrabold text-[#D4AF37] mt-1">{bpaiMarket.opportunity_score}<span className="text-lg text-[#c9c39f]"> / 100</span></p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[["Demand", bpaiMarket.demand_level], ["Recurrence", bpaiMarket.recurrence], ["Potential", bpaiMarket.business_potential]].map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-gray-100 bg-white p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-[#687280]">{k}</p>
                      <p className="text-sm font-bold text-[#0F0F0F] mt-1">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#9aa1ab] leading-relaxed">These insights are directional estimates intended to help founders evaluate opportunities. Validate your market before building.</p>
                <button onClick={gateMarketContinue} className="w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="bpai-market-continue">Continue</button>
              </div>
            )}

            {/* Blueprint AI — Q3 platform */}
            {phase === "bpai_q3" && (
              <div className="bp-msg-in" data-testid="bpai-q3">
                <button onClick={() => setPhase("bpai_market")} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] mb-3 transition-colors"><ArrowLeft size={14} /> Back</button>
                <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] leading-snug">Where would you like to build your startup?</h3>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {BPAI_PLATFORMS.map((p) => (
                    <button key={p.name} onClick={() => runBpaiBlueprint(p.name)} className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-[#1f2937] hover:border-[#D4AF37] hover:bg-[#FBF7EA] hover:shadow-md transition-all flex items-center gap-2" data-testid={`bpai-platform-${p.name}`}>
                      <BuilderLogo url={p.url} name={p.name} size={24} />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Workflow — intro / ask profession */}
            {phase === "aiw_intro" && (
              <div className="bp-msg-in" data-testid="aiw-intro">
                <button onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] mb-3 transition-colors"><ArrowLeft size={14} /> Back</button>
                <div className="rounded-2xl bg-[#FBF7EA] border border-[#EBDFB8] p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a] flex items-center gap-1.5"><Zap size={13} className="text-[#D4AF37]" /> Blueprint AI</p>
                  <p className="text-sm text-[#5c4a12] mt-2 leading-relaxed">Welcome! I'll help you discover AI workflows that can save you time.</p>
                </div>
                <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] mt-4">What do you do for work?</h3>
                <input
                  value={aiwInput}
                  onChange={(e) => setAiwInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runWorkflowAudit()}
                  placeholder="e.g. Teacher, Realtor, Accountant…"
                  className="mt-3 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                  data-testid="aiw-profession-input"
                />
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {AIW_PROFESSIONS.map((p) => (
                    <button key={p} onClick={() => setAiwInput(p)} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors" data-testid={`aiw-profession-${p}`}>{p}</button>
                  ))}
                </div>
                <button onClick={runWorkflowAudit} disabled={!aiwInput.trim()} className="mt-5 w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-40" data-testid="aiw-continue-btn">Continue</button>

                <button onClick={() => beginCountryIdeas()} className="mt-3 w-full rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-[#D4AF37] hover:shadow-md transition-all flex items-center gap-3" data-testid="aiw-country-btn">
                  <span className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><Globe size={18} /></span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 font-bold text-sm text-[#0F0F0F]">🌍 AI Country Ideas <span className="text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">NEW</span></span>
                    <span className="block text-xs text-[#687280] mt-0.5">10 startup ideas tailored to any country's market.</span>
                  </span>
                </button>
              </div>
            )}

            {/* AI Workflow — results */}
            {phase === "aiw_results" && aiwData && (
              <div className="space-y-3 bp-msg-in" data-testid="aiw-results">
                <button onClick={reset} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors"><ArrowLeft size={14} /> Back</button>
                <div className="flex items-center gap-2">
                  <Zap size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">AI Workflow Audit</h3>
                </div>
                <p className="text-sm text-[#687280] -mt-1">Tailored for a {aiwProfession}.</p>

                {aiwData.workflows.map((w, i) => (
                  <WorkflowCard key={i} w={w} idx={i} expanded={aiwExpanded === i} onToggle={() => setAiwExpanded(aiwExpanded === i ? null : i)} onLearn={openLearn} onBuild={buildWorkflow} />
                ))}

                <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-4 text-center" data-testid="aiw-summary">
                  <p className="text-[11px] uppercase tracking-wide text-[#c9c39f]">Estimated Time Saved</p>
                  <p className="font-display text-3xl font-extrabold text-[#D4AF37] mt-1">{aiwData.total_hours} Hours <span className="text-sm text-[#c9c39f] font-semibold">Every Week</span></p>
                  <p className="text-xs text-[#c9c39f] mt-1">≈ {aiwData.yearly_hours} Hours Per Year</p>
                </div>

                {aiwExtras.length > 0 && aiwExtras.map((w, i) => (
                  <WorkflowCard key={`x-${i}`} w={w} idx={`extra-${i}`} onLearn={openLearn} onBuild={buildWorkflow} />
                ))}

                {/* Find Another Workflow */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mb-2 flex items-center gap-1.5"><Search size={13} /> Find Another Workflow</p>
                  <div className="flex gap-2">
                    <input
                      value={aiwSearch}
                      onChange={(e) => setAiwSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && runWorkflowSearch()}
                      placeholder="Search another workflow..."
                      className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]"
                      data-testid="aiw-search-input"
                    />
                    <button onClick={() => runWorkflowSearch()} disabled={!aiwSearch.trim() || aiwSearching} className="shrink-0 rounded-full bg-[#0F0F0F] text-white px-4 text-xs font-semibold hover:bg-[#1c1c1c] disabled:opacity-50 flex items-center" data-testid="aiw-search-btn">{aiwSearching ? <Loader2 size={14} className="animate-spin" /> : "Go"}</button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {AIW_SEARCH_EXAMPLES.map((s) => (
                      <button key={s} onClick={() => runWorkflowSearch(s)} disabled={aiwSearching} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors disabled:opacity-50" data-testid={`aiw-search-example-${s}`}>{s}</button>
                    ))}
                  </div>
                </div>

                {/* Expand Vision */}
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#687280] flex items-center gap-1.5"><Sparkles size={13} /> Expand Vision</p>
                  <p className="text-xs text-[#687280] mt-1">Describe a task that takes too much time.</p>
                  <textarea
                    value={aiwVision}
                    onChange={(e) => setAiwVision(e.target.value)}
                    rows={3}
                    placeholder="Example: I spend hours replying to customer emails."
                    className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] resize-none"
                    data-testid="aiw-vision-input"
                  />
                  <button onClick={runWorkflowExpand} disabled={!aiwVision.trim() || aiwVisionLoading} className="mt-2 w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5" data-testid="aiw-vision-btn">{aiwVisionLoading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Generate</button>
                  {aiwVisionResult && <div className="mt-3"><WorkflowCard w={aiwVisionResult} idx="vision" onLearn={openLearn} onBuild={buildWorkflow} /></div>}
                </div>

                <button onClick={() => { closePanel(); navigate("/ai-resources/roadmap"); }} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a]" data-testid="aiw-roadmap-btn"><Map size={13} /> Founders Roadmap</button>
              </div>
            )}

            {/* AI Country Ideas — setup */}
            {phase === "country_setup" && (
              <div className="space-y-4 bp-msg-in" data-testid="country-setup">
                <button onClick={beginWorkflow} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors"><ArrowLeft size={14} /> Back</button>
                <div className="flex items-center gap-2">
                  <Globe size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">AI Country Ideas</h3>
                </div>
                {countryImage && (
                  <div className="flex items-center gap-2 rounded-xl bg-[#FBF7EA] border border-[#EBDFB8] p-2" data-testid="country-scanner-photo">
                    <img src={countryImage.startsWith("data:") ? countryImage : `data:image/jpeg;base64,${countryImage}`} alt="Scan" className="w-10 h-10 rounded-lg object-cover" />
                    <p className="text-xs text-[#8a6d1a] font-semibold">Using your scanned photo — ideas will combine it with the country's market.</p>
                  </div>
                )}

                {/* Step 1 — country */}
                <div>
                  <p className="text-sm font-bold text-[#0F0F0F] mb-2">1. Which country are you building in?</p>
                  <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
                    <input
                      value={countryQuery}
                      onChange={(e) => { setCountryQuery(e.target.value); setCountrySel(null); }}
                      placeholder="Search country..."
                      className="w-full rounded-2xl border border-gray-200 pl-9 pr-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                      data-testid="country-search-input"
                    />
                  </div>
                  {countrySel ? (
                    <div className="mt-2 flex items-center justify-between rounded-xl bg-[#0F0F0F] text-white px-3 py-2.5" data-testid="country-selected">
                      <span className="text-sm font-bold">{countrySel.flag} {countrySel.name}</span>
                      <button onClick={() => { setCountrySel(null); setCountryQuery(""); }} className="text-[#c9c39f] hover:text-white"><X size={16} /></button>
                    </div>
                  ) : (
                    <div className="mt-2 max-h-40 overflow-y-auto flex flex-wrap gap-1.5" data-testid="country-options">
                      {(countryQuery.trim()
                        ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(countryQuery.trim().toLowerCase())).slice(0, 24)
                        : COUNTRIES.filter((c) => POPULAR_COUNTRIES.includes(c.name))
                      ).map((c) => (
                        <button key={c.code} onClick={() => { setCountrySel(c); setCountryQuery(c.name); }} className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors" data-testid={`country-option-${c.code}`}>{c.flag} {c.name}</button>
                      ))}
                      {countryQuery.trim() && COUNTRIES.filter((c) => c.name.toLowerCase().includes(countryQuery.trim().toLowerCase())).length === 0 && (
                        <p className="text-xs text-[#9aa1ab] py-2">No match — try another spelling.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Step 2 — build */}
                <div>
                  <p className="text-sm font-bold text-[#0F0F0F] mb-2">2. What would you like to build?</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="country-build-options">
                    {COUNTRY_BUILD_TYPES.map((b) => (
                      <button key={b} onClick={() => setCountryBuild(countryBuild === b ? "" : b)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${countryBuild === b ? "bg-[#D4AF37] text-[#0F0F0F] border-[#D4AF37]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"}`} data-testid={`country-build-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{b}</button>
                    ))}
                  </div>
                </div>

                {/* Step 3 — audience (optional) OR Small Business idea type */}
                {countryBuild === SMALL_BIZ ? (
                  <div>
                    <p className="text-sm font-bold text-[#0F0F0F] mb-2">3. What type of ideas would you like?</p>
                    <div className="space-y-2" data-testid="smallbiz-category-options">
                      {SMALL_BIZ_CATEGORIES.map((c) => (
                        <button
                          key={c.key}
                          onClick={() => setCountrySmallCat(countrySmallCat === c.key ? "" : c.key)}
                          className={`w-full text-left rounded-2xl border p-3 transition-colors ${countrySmallCat === c.key ? "border-[#D4AF37] bg-[#FBF7EA]" : "border-gray-200 bg-white hover:border-[#D4AF37]"}`}
                          data-testid={`smallbiz-cat-${c.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          <p className="text-sm font-bold text-[#0F0F0F]">{c.emoji} {c.key}</p>
                          <p className="text-xs text-[#687280] mt-0.5">{c.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-bold text-[#0F0F0F] mb-2">3. Who do you want to serve? <span className="text-[11px] font-normal text-[#9aa1ab]">(optional)</span></p>
                    <div className="flex flex-wrap gap-1.5" data-testid="country-audience-options">
                      {COUNTRY_AUDIENCES.map((a) => (
                        <button key={a} onClick={() => setCountryAudience(countryAudience === a ? "" : a)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${countryAudience === a ? "bg-[#D4AF37] text-[#0F0F0F] border-[#D4AF37]" : "bg-white text-[#374151] border-gray-200 hover:border-[#D4AF37]"}`} data-testid={`country-audience-${a.toLowerCase()}`}>{a}</button>
                      ))}
                    </div>
                  </div>
                )}

                {countryBuild === SMALL_BIZ ? (
                  <button onClick={() => runSmallBusinessIdeas(false)} disabled={!countrySel || !countrySmallCat} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-40" data-testid="smallbiz-generate-btn">
                    <Sparkles size={16} /> Generate Small Business Ideas
                  </button>
                ) : (
                  <button onClick={runCountryIdeas} disabled={!countrySel} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-40" data-testid="country-generate-btn">
                    <Sparkles size={16} /> Generate Country Ideas
                  </button>
                )}
              </div>
            )}

            {/* AI Country Ideas — results */}
            {phase === "country_results" && countryData && (
              <div className="space-y-3 bp-msg-in" data-testid="country-results">
                <button onClick={() => setPhase("country_setup")} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors"><ArrowLeft size={14} /> Back</button>
                <div className="flex items-center gap-2">
                  <Globe size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">{countrySel?.flag} {countryData.country} Ideas</h3>
                </div>
                <p className="text-sm text-[#687280] -mt-1">10 startup ideas tailored to this market{countryBuild && countryBuild !== "Doesn't Matter" ? ` · ${countryBuild}` : ""}.</p>

                {countryData.ideas.map((idea, i) => (
                  <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm" data-testid={`country-idea-${i}`}>
                    <div className="flex items-start gap-3">
                      <span className="w-12 h-12 shrink-0 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex flex-col items-center justify-center leading-none">
                        <span className="text-lg font-extrabold">{idea.opportunity_score}</span>
                        <span className="text-[8px] text-[#c9c39f] font-semibold">/100</span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <h5 className="font-display text-sm font-bold text-[#0F0F0F]">{idea.name}</h5>
                        {idea.problem && <p className="text-xs text-[#687280] mt-0.5"><span className="font-semibold text-[#374151]">Problem:</span> {idea.problem}</p>}
                      </div>
                    </div>
                    {idea.target_customers && <p className="text-xs text-[#687280] mt-2"><span className="font-semibold text-[#374151]">For:</span> {idea.target_customers}</p>}
                    {idea.why_country && <p className="text-xs text-[#687280] mt-1"><span className="font-semibold text-[#374151]">Why {countryData.country}?</span> {idea.why_country}</p>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBF7EA] text-[#8a6d1a]">{idea.revenue_model}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-[#374151]">{idea.difficulty}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <button onClick={() => buildFromCountryIdea(idea)} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F0F0F] text-white py-2 text-xs font-bold hover:bg-black transition-colors" data-testid={`country-idea-${i}-blueprint`}>
                        <Sparkles size={13} /> Generate Blueprint
                      </button>
                      <button onClick={() => saveCountryIdea(idea)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white text-[#374151] py-2 text-xs font-bold hover:border-[#D4AF37] transition-colors" data-testid={`country-idea-${i}-save`}>
                        <Save size={13} /> Save Idea
                      </button>
                    </div>
                  </div>
                ))}

                {/* Resources for your country */}
                {countryData.resources && countryData.resources.length > 0 && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-4" data-testid="country-resources">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mb-2.5 flex items-center gap-1.5"><Building2 size={13} className="text-[#D4AF37]" /> Resources for {countryData.country}</p>
                    <div className="space-y-1.5">
                      {countryData.resources.map((r, i) => (
                        <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2 hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors group" data-testid={`country-resource-${i}`}>
                          <span className="min-w-0">
                            <span className="block text-[11px] font-bold text-[#8a6d1a] uppercase tracking-wide">{r.label}</span>
                            <span className="block text-xs text-[#374151] truncate">{r.name}</span>
                          </span>
                          <ExternalLink size={14} className="text-[#9aa1ab] group-hover:text-[#D4AF37] shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => beginCountryIdeas()} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a]" data-testid="country-restart-btn"><Globe size={13} /> Try Another Country</button>
              </div>
            )}

            {/* Small Business Ideas — results */}
            {phase === "smallbiz_results" && smallBizData && (
              <div className="space-y-3 bp-msg-in" data-testid="smallbiz-results">
                <button onClick={() => setPhase("country_setup")} className="flex items-center gap-1 text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] transition-colors"><ArrowLeft size={14} /> Back</button>
                <div className="flex items-center gap-2">
                  <Building2 size={18} className="text-[#D4AF37]" />
                  <h3 className="font-display text-xl font-extrabold text-[#0F0F0F]">Small Business Ideas</h3>
                </div>
                <p className="text-sm text-[#687280] -mt-1">{smallBizData.category} · proven concepts from around the world, adaptable to {smallBizData.country}.</p>

                {smallBizData.ideas.map((idea, i) => (
                  <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm" data-testid={`smallbiz-idea-${i}`}>
                    <div className="flex items-start gap-3">
                      <span className="w-12 h-12 shrink-0 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex flex-col items-center justify-center leading-none">
                        <span className="text-lg font-extrabold">{idea.opportunity_score}</span>
                        <span className="text-[8px] text-[#c9c39f] font-semibold">/100</span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <h5 className="font-display text-sm font-bold text-[#0F0F0F]">{idea.name}</h5>
                        <p className="text-xs text-[#687280] mt-0.5 flex items-center gap-1"><span className="text-sm">{idea.origin_flag}</span> From {idea.origin_country}</p>
                      </div>
                    </div>
                    {idea.why_works && <p className="text-xs text-[#687280] mt-2"><span className="font-semibold text-[#374151]">Why it works:</span> {idea.why_works}</p>}
                    {idea.how_local && <p className="text-xs text-[#687280] mt-1"><span className="font-semibold text-[#374151]">In {smallBizData.country}:</span> {idea.how_local}</p>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBF7EA] text-[#8a6d1a]">Startup cost: {idea.startup_cost}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <button onClick={() => buildFromSmallBizIdea(idea)} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0F0F0F] text-white py-2 text-xs font-bold hover:bg-black transition-colors" data-testid={`smallbiz-idea-${i}-blueprint`}>
                        <Sparkles size={13} /> Generate Blueprint
                      </button>
                      <button onClick={() => saveSmallBizIdea(idea)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white text-[#374151] py-2 text-xs font-bold hover:border-[#D4AF37] transition-colors" data-testid={`smallbiz-idea-${i}-save`}>
                        <Save size={13} /> Save Idea
                      </button>
                    </div>
                  </div>
                ))}

                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => runSmallBusinessIdeas(true)} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-bold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a] transition-colors" data-testid="smallbiz-refresh-btn"><RefreshCw size={13} /> Refresh Ideas</button>
                  <button onClick={() => smallBizData.ideas[0] && buildFromSmallBizIdea(smallBizData.ideas[0])} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-xs font-bold hover:bg-[#c5a030] transition-colors" data-testid="smallbiz-blueprint-top-btn"><Sparkles size={13} /> Generate Blueprint</button>
                </div>
                <button onClick={() => beginCountryIdeas()} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a]" data-testid="smallbiz-restart-btn"><Globe size={13} /> Start Over</button>
              </div>
            )}


            {messages.map((m, i) => (
              <div key={i} className={`bp-msg-in flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "advice" ? (
                  <div className="max-w-[90%] rounded-2xl bg-[#FBF7EA] border border-[#EBDFB8] px-3.5 py-2.5 text-sm text-[#8a6d1a] flex gap-2">
                    <Sparkles size={15} className="mt-0.5 shrink-0 text-[#D4AF37]" /> <span>{m.text}</span>
                  </div>
                ) : (
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-[#0F0F0F] text-white" : "bg-white border border-gray-100 text-[#1f2937] shadow-sm"}`}>
                    {m.text}
                  </div>
                )}
              </div>
            ))}

            {busy && (phase === "interview" || phase === "discovery" || phase === "generating" || phase === "reco_loading") && (
              <div className="flex justify-start bp-msg-in">
                <div className="rounded-2xl bg-white border border-gray-100 px-4 py-3 shadow-sm flex gap-1">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {phase === "reco" && (
              <div className="space-y-3">
                {recos.map((r) => (
                  <div key={r.id || r.title} className="rounded-2xl bg-white border border-gray-100 p-4 shadow-sm bp-msg-in" data-testid={`blueprint-reco-${r.id || "x"}`}>
                    <p className="text-[10px] font-semibold text-[#D4AF37] uppercase">{r.category_name}</p>
                    <p className="font-display font-bold text-[#0F0F0F] mt-0.5">⭐ {r.title}</p>
                    <p className="text-xs text-[#687280] mt-1 line-clamp-2">{r.summary}</p>
                    <div className="flex gap-2 mt-3">
                      {r.id && (
                        <button onClick={() => { setOpen(false); navigate(`/business/${r.id}`); }} className="flex-1 rounded-full border border-gray-200 py-2 text-xs font-semibold hover:border-[#D4AF37] transition-colors" data-testid={`reco-view-${r.id}`}>
                          View Idea
                        </button>
                      )}
                      <button onClick={() => buildFromIdea(r)} className="flex-1 rounded-full bg-[#0F0F0F] text-white py-2 text-xs font-semibold hover:bg-[#1c1c1c] transition-colors" data-testid={`reco-build-${r.id || "x"}`}>
                        Build This
                      </button>
                    </div>
                  </div>
                ))}
                <button onClick={refreshRecos} disabled={recoBusy} className="w-full rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#0F0F0F] flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="reco-refresh-btn">
                  {recoBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Show me 3 different ideas
                </button>
              </div>
            )}

            {/* Blueprint */}
            {phase === "blueprint" && blueprint && (
              <div className="space-y-3 bp-msg-in" data-testid="blueprint-result">
                <ResultPaywall label="Blueprint" testid="blueprint-paywall">
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white px-4 py-3">
                    <p className="text-[10px] uppercase tracking-wide text-[#c9c39f]">Your Startup Blueprint</p>
                    <p className="font-display text-lg font-bold">{blueprint.project_name}</p>
                  </div>
                  <div className="p-4 space-y-3">
                    {BP_SECTIONS.slice(1).filter(([k]) => blueprint[k] && (!Array.isArray(blueprint[k]) || blueprint[k].length)).map(([k, label]) => (
                      <div key={k}>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">{label}</p>
                        {Array.isArray(blueprint[k]) ? (
                          <ul className="mt-1 space-y-0.5">
                            {blueprint[k].map((x, idx) => <li key={idx} className="text-sm text-[#374151] flex gap-1.5"><Check size={13} className="mt-1 shrink-0 text-[#D4AF37]" />{x}</li>)}
                          </ul>
                        ) : (
                          <p className="text-sm text-[#374151] mt-0.5">{blueprint[k]}</p>
                        )}
                      </div>
                    ))}
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37]">Complete AI Builder Prompt</p>
                      <pre className="mt-1 text-xs text-[#374151] whitespace-pre-wrap bg-[#F7F7F7] rounded-lg p-3 max-h-40 overflow-y-auto">{blueprint.builder_prompt}</pre>
                    </div>
                  </div>
                </div>

                <button onClick={downloadLaunchPlan} disabled={launchBusy} className="w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="bpai-download-launch-plan">
                  {launchBusy ? <><Loader2 size={15} className="animate-spin" /> Generating Launch Plan…</> : <><Download size={15} /> 📥 Download Launch Plan</>}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button onClick={copyBlueprint} className="rounded-full bg-[#0F0F0F] text-white py-2.5 text-xs font-semibold hover:bg-[#1c1c1c] flex items-center justify-center gap-1.5" data-testid="blueprint-copy-btn"><Copy size={13} /> Copy Prompt</button>
                  <button onClick={downloadPdf} className="rounded-full border border-gray-200 py-2.5 text-xs font-semibold hover:border-[#D4AF37] flex items-center justify-center gap-1.5" data-testid="blueprint-pdf-btn"><Download size={13} /> Download PDF</button>
                </div>
                <button onClick={doSave} disabled={busy} className="w-full rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-xs font-semibold hover:bg-[#c5a030] flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="blueprint-save-btn">{savedId ? <Check size={13} /> : <Save size={13} />} {savedId ? "Saved" : "Save Blueprint"}</button>
                </ResultPaywall>

                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-[#687280] mt-2 mb-2">{bpaiPlatform ? "Your Builder" : "Continue Building"}</p>
                  <div className="space-y-2">
                    {(bpaiPlatform
                      ? [BUILDERS.find((x) => x.name === bpaiPlatform) || { name: bpaiPlatform, desc: "Your chosen builder.", url: (BPAI_PLATFORMS.find((x) => x.name === bpaiPlatform) || {}).url || "" }]
                      : BUILDERS
                    ).map((b) => (
                      <div key={b.name} className={`flex items-center gap-3 rounded-xl border p-3 ${b.recommended ? "border-[#D4AF37] bg-[#FBF7EA]" : "border-gray-100 bg-white"}`} data-testid={`builder-${b.name.toLowerCase()}`}>
                        <BuilderLogo url={b.url} name={b.name} size={32} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0F0F0F] flex items-center gap-1.5">{b.name}{b.recommended && <span className="text-[9px] bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">RECOMMENDED</span>}</p>
                          <p className="text-[11px] text-[#687280]">{b.desc}</p>
                        </div>
                        <button onClick={() => openBuilder(b)} className="shrink-0 rounded-full bg-[#0F0F0F] text-white px-3 py-1.5 text-[11px] font-semibold hover:bg-[#1c1c1c] flex items-center gap-1" data-testid={`open-builder-${b.name.toLowerCase()}`}><ExternalLink size={11} /> Open</button>
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={() => { closePanel(); navigate("/ai-resources/roadmap"); }} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#687280] hover:border-[#D4AF37] hover:text-[#8a6d1a] mt-1" data-testid="blueprint-roadmap-btn"><Map size={13} /> Founders Roadmap</button>
              </div>
            )}

            {/* My Blueprints */}
            {phase === "list" && (
              <div className="space-y-2 bp-msg-in" data-testid="blueprint-list">
                <p className="font-display font-bold text-[#0F0F0F]">My Blueprints</p>
                {myList.length === 0 ? (
                  <p className="text-sm text-[#687280]">No saved blueprints yet. Create one and hit Save.</p>
                ) : myList.map((b) => (
                  <div key={b.id} onClick={() => openSaved(b.id)} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-3 cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid={`saved-blueprint-${b.id}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center shrink-0"><Rocket size={15} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F0F0F] truncate">{b.project_name}</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span className="text-[11px] text-[#687280]">{new Date(b.updated_at).toLocaleDateString()}</span>
                        {b.platform && <span className="text-[9px] font-semibold bg-[#F7F7F7] text-[#374151] px-1.5 py-0.5 rounded-full">{b.platform}</span>}
                        <span className="text-[9px] font-semibold bg-[#FBF7EA] text-[#8a6d1a] px-1.5 py-0.5 rounded-full">{b.status || "Ready"}</span>
                      </div>
                    </div>
                    <button onClick={(e) => duplicateBlueprint(b.id, e)} title="Duplicate" className="p-1.5 text-[#687280] hover:text-[#D4AF37]" data-testid={`duplicate-blueprint-${b.id}`}><Copy size={14} /></button>
                    <button onClick={(e) => removeSaved(b.id, e)} title="Delete" className="p-1.5 text-[#687280] hover:text-red-500" data-testid={`delete-blueprint-${b.id}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Input / chips */}
          {(phase === "interview" || phase === "discovery") && (
            <div className="border-t border-gray-100 bg-white p-3">
              {activeStep?.chips && (() => {
                const useCats = phase === "discovery" && activeStep.key === "interests" && catChips.length > 0;
                const chipList = useCats ? catChips : activeStep.chips.map((c) => ({ label: c, value: c }));
                return (
                  <div className="flex flex-wrap gap-1.5 mb-2.5 max-h-28 overflow-y-auto" data-testid="blueprint-chips">
                    {chipList.map((c) => (
                      <button key={c.value} onClick={() => submitAnswer(c.value)} disabled={busy} className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-[#374151] hover:border-[#D4AF37] hover:bg-[#FBF7EA] transition-colors disabled:opacity-50" data-testid={`chip-${c.label}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitAnswer()}
                  placeholder={activeStep?.placeholder || "Type your answer…"}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]"
                  data-testid="blueprint-input"
                />
                <button onClick={() => submitAnswer()} disabled={busy || !input.trim()} className="shrink-0 w-10 h-10 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center hover:bg-[#c5a030] transition-colors disabled:opacity-50" data-testid="blueprint-send-btn">
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}

          {phase === "welcome" && (
            <div className="border-t border-gray-100 bg-white p-3">
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && input.trim() && beginHaveIdea(input.trim())}
                  placeholder="Tell me about your idea..."
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]"
                  data-testid="blueprint-welcome-input"
                />
                <button onClick={() => input.trim() && beginHaveIdea(input.trim())} disabled={!input.trim()} className="shrink-0 w-10 h-10 rounded-full bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center hover:bg-[#c5a030] transition-colors disabled:opacity-50" data-testid="blueprint-welcome-send">
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Learn This Workflow popup */}
          {learnWf && (
            <div className="absolute inset-0 z-20 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={() => setLearnWf(null)} data-testid="workflow-learn-popup">
              <div className="bg-white rounded-2xl w-full max-w-[400px] max-h-[88%] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white px-4 py-3 flex items-start justify-between gap-2 rounded-t-2xl">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-[#c9c39f]">AI Workflow</p>
                    <p className="font-display text-base font-bold">{learnWf.name}</p>
                  </div>
                  <button onClick={() => setLearnWf(null)} className="text-white/70 hover:text-white shrink-0" data-testid="workflow-learn-close"><X size={18} /></button>
                </div>
                {!learnInfo ? (
                  <div className="py-12 flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={22} /></div>
                ) : (
                  <div className="p-4 space-y-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mb-1">What is this?</p>
                      <p className="text-sm text-[#374151] leading-relaxed">{learnInfo.what}</p>
                    </div>
                    {learnInfo.best_for?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mb-1.5">Best For</p>
                        <div className="flex flex-wrap gap-1.5">{learnInfo.best_for.map((b) => <span key={b} className="rounded-full bg-[#F7F7F7] border border-gray-100 px-2.5 py-1 text-[11px] font-medium text-[#374151]">{b}</span>)}</div>
                      </div>
                    )}
                    {learnInfo.benefits?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mb-1.5">Benefits</p>
                        <ul className="space-y-1">{learnInfo.benefits.map((b) => <li key={b} className="text-sm text-[#374151] flex gap-1.5"><Check size={14} className="mt-0.5 shrink-0 text-green-500" />{b}</li>)}</ul>
                      </div>
                    )}
                    {learnInfo.youll_learn?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mb-1.5">You'll Learn</p>
                        <ul className="space-y-1">{learnInfo.youll_learn.map((b) => <li key={b} className="text-sm text-[#374151] flex gap-1.5"><ArrowRight size={13} className="mt-1 shrink-0 text-[#D4AF37]" />{b}</li>)}</ul>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <button onClick={() => setLearnWf(null)} className="rounded-full border border-gray-200 py-2.5 text-xs font-semibold text-[#374151] hover:border-[#0F0F0F]" data-testid="workflow-learn-dismiss">Close</button>
                      <button onClick={() => buildWorkflow(learnWf)} className="rounded-full bg-[#D4AF37] text-[#0F0F0F] py-2.5 text-xs font-bold hover:bg-[#c5a030] flex items-center justify-center gap-1.5" data-testid="workflow-learn-build"><Rocket size={13} /> Build This</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </>
      )}
    </>
  );
};
